import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Activity, Heart, Brain, ChevronDown, Info, Target, TrendingUp } from 'lucide-react';
import { WorkoutPlayer2 } from './workouts/player/WorkoutPlayer2';

interface WorkoutSuggestionProps {
  readiness: number;
  recovery: number;
}

export function WorkoutSuggestion({ readiness, recovery }: WorkoutSuggestionProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isWorkoutActive, setIsWorkoutActive] = useState(false);

  const workout = {
    name: recovery > 80 ? 'High Intensity Training' : 'Recovery Session',
    duration: recovery > 80 ? 45 : 30,
    targetStrain: recovery > 80 ? 14.5 : 6.0,
    intensity: recovery > 80 ? 'High' : 'Light',
    description: recovery > 80 
      ? 'High-intensity intervals optimized for your peak recovery state'
      : 'Active recovery session to promote regeneration',
    benefits: recovery > 80 
      ? [
          'Maximizes calorie burn',
          'Improves cardiovascular fitness',
          'Boosts metabolic rate',
          'Enhances endurance'
        ]
      : [
          'Promotes muscle recovery',
          'Reduces muscle tension',
          'Improves blood flow',
          'Enhances mobility'
        ]
  };

  if (isWorkoutActive) {
    return (
      <WorkoutPlayer2
        workout={{
          id: 'suggested-workout',
          name: workout.name,
          exercises: [
            { name: 'Warm Up', duration: 300, completed: false, intensity: 'Light' },
            { name: 'Main Set', duration: 1800, completed: false, intensity: workout.intensity },
            { name: 'Cool Down', duration: 300, completed: false, intensity: 'Light' }
          ],
          targetStrain: workout.targetStrain
        }}
        onClose={() => setIsWorkoutActive(false)}
      />
    );
  }

  return (
    <div className="theme-card relative overflow-hidden">
      {/* Siri-like Glow Container */}
      <div className="relative p-[1px] rounded-lg group">
        {/* Animated Border */}
        <div className="absolute inset-0 rounded-lg animate-siri-glow">
          <div className="absolute inset-0 bg-gradient-to-r from-primary/30 via-purple-500/30 to-primary/30 rounded-lg animate-border-flow" />
        </div>

        {/* Inner Content */}
        <div className="relative bg-background rounded-lg">
          {/* Ambient Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-purple-500/5 to-transparent opacity-30" />
          
          {/* Content */}
          <div className="relative">
            <div className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Activity className="w-5 h-5 text-primary" />
                    <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full" />
                  </div>
                  <div>
                    <h3 className="text-sm font-medium">Today's Recommended Workout</h3>
                    <p className="text-sm text-muted-foreground">
                      {workout.name} • {workout.duration}min • {workout.intensity}
                    </p>
                  </div>
                </div>
                <button 
                  onClick={() => setIsWorkoutActive(true)}
                  className="theme-button px-3 py-1.5 rounded-lg text-sm flex items-center gap-1.5"
                >
                  <Play className="w-3.5 h-3.5" />
                  Start
                </button>
              </div>

              {/* Expandable Content */}
              <AnimatePresence initial={false}>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0, marginTop: 0 }}
                    animate={{ height: 'auto', opacity: 1, marginTop: 16 }}
                    exit={{ height: 0, opacity: 0, marginTop: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden border-t border-border/50 pt-4 space-y-4"
                  >
                    {/* Metrics */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-muted/30 p-3 rounded-lg">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Brain className="w-4 h-4" />
                          <span>Readiness</span>
                        </div>
                        <div className="text-lg font-semibold">{readiness}%</div>
                      </div>
                      
                      <div className="bg-muted/30 p-3 rounded-lg">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Heart className="w-4 h-4" />
                          <span>Recovery</span>
                        </div>
                        <div className="text-lg font-semibold">{recovery}%</div>
                      </div>
                      
                      <div className="bg-muted/30 p-3 rounded-lg">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Target className="w-4 h-4" />
                          <span>Target</span>
                        </div>
                        <div className="text-lg font-semibold">{workout.targetStrain}</div>
                      </div>
                    </div>

                    {/* Recommendation Explanation */}
                    <div className="bg-primary/10 rounded-lg p-4 space-y-3">
                      <div className="flex items-center gap-2">
                        <Info className="w-4 h-4 text-primary" />
                        <span className="font-medium">Why This Workout?</span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {recovery > 80 
                          ? `With your excellent recovery (${recovery}%) and readiness (${readiness}%), your body is primed for high-intensity training. This workout is designed to capitalize on your peak state.`
                          : `Based on your current recovery (${recovery}%) and readiness (${readiness}%), a recovery-focused session will help optimize your body's adaptation and prevent overtraining.`
                        }
                      </p>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm">
                          <TrendingUp className="w-4 h-4 text-primary" />
                          <span className="font-medium">Benefits</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          {workout.benefits.map((benefit, index) => (
                            <div
                              key={index}
                              className="flex items-center gap-2 text-xs bg-primary/20 text-primary px-2 py-1 rounded-full"
                            >
                              <div className="w-1 h-1 bg-primary rounded-full" />
                              <span>{benefit}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Expand/Collapse Button */}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="w-full p-2 flex items-center justify-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors border-t border-border/50"
            >
              <span>{isExpanded ? 'Less Info' : 'More Info'}</span>
              <motion.div
                animate={{ rotate: isExpanded ? 180 : 0 }}
                transition={{ duration: 0.2 }}
              >
                <ChevronDown className="w-4 h-4" />
              </motion.div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}